import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { FolderHeart, Plus, X, ArrowRight, Trash2, Pencil, Share2 } from "lucide-react";
import { toast } from "sonner";
import AutocompleteInput from "@/components/AutocompleteInput";
import RenameModal from "@/components/RenameModal";

export default function Galleries() {
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [renaming, setRenaming] = useState(null); // gallery being renamed
  const navigate = useNavigate();

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/galleries");
      setGalleries(r.data.galleries || []);
    } catch (e) {
      toast.error("Failed to load galleries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const del = async (id, name) => {
    if (!window.confirm(`Delete gallery "${name}"? Clips will remain in your library — only the client view is removed.`)) return;
    try {
      await api.delete(`/galleries/${id}`);
      toast.success("Gallery deleted");
      refresh();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="px-6 md:px-12 py-10 max-w-[1300px]" data-testid="galleries-page">
      <div className="flex items-end justify-between mb-8 gap-6">
        <div className="border-l-4 border-orange-600 pl-5">
          <div className="label-tiny mb-2">Client Galleries</div>
          <h1 className="font-display uppercase text-5xl font-bold leading-[0.95]">Galleries</h1>
          <p className="text-stone-400 mt-3 max-w-xl">
            Curated folders of clips you share with specific clients. Assign clients, pick a branded reel template, drop in clips.
          </p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary inline-flex items-center gap-2" data-testid="create-gallery-btn">
          <Plus size={14} /> New Gallery
        </button>
      </div>

      {loading ? (
        <div className="text-stone-500 py-12 text-center">Loading…</div>
      ) : galleries.length === 0 ? (
        <div className="border border-dashed border-stone-800 p-16 text-center" data-testid="galleries-empty">
          <FolderHeart size={32} className="mx-auto text-stone-600 mb-3" />
          <p className="text-stone-400">No galleries yet.</p>
          <p className="text-stone-500 text-sm mt-2">Create your first one and assign clips + a client login.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="galleries-grid">
          {galleries.map((g) => (
            <div
              key={g.id}
              className="border border-stone-800 hover:border-orange-700 transition-colors group"
              data-testid={`gallery-card-${g.id}`}
            >
              <button onClick={() => navigate(`/galleries/${g.id}`)} className="block w-full text-left p-4">
                <div className="flex items-start justify-between mb-2 gap-2">
                  <div className="font-display uppercase font-bold text-xl truncate flex-1">{g.name}</div>
                  <ArrowRight size={14} className="text-stone-500 group-hover:text-orange-500 transition-colors mt-1.5 shrink-0" />
                </div>
                <div className="mb-2">
                  {g.kind === "social_media" ? (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-widest bg-fuchsia-900/40 border border-fuchsia-700 text-fuchsia-300" data-testid={`gallery-kind-social-${g.id}`}>
                      <Share2 size={9} /> Social Media
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-widest bg-stone-900 border border-stone-700 text-stone-400" data-testid={`gallery-kind-client-${g.id}`}>
                      <FolderHeart size={9} /> Client
                    </span>
                  )}
                </div>
                {g.description && <div className="text-xs text-stone-500 line-clamp-2 mb-3">{g.description}</div>}
                <div className="font-mono-tc text-xs text-stone-500 flex gap-4">
                  <span><span className="text-orange-500 text-base">{g.clip_count || 0}</span> clip{g.clip_count !== 1 ? "s" : ""}</span>
                  {g.kind !== "social_media" && (
                    <span><span className="text-orange-500 text-base">{g.client_count || 0}</span> client{g.client_count !== 1 ? "s" : ""}</span>
                  )}
                </div>
              </button>
              <div className="flex border-t border-stone-800">
                <button
                  onClick={() => setRenaming(g)}
                  className="px-3 py-2 text-stone-500 hover:text-orange-400 transition-colors"
                  title="Rename gallery"
                  data-testid={`gallery-rename-${g.id}`}
                >
                  <Pencil size={13} />
                </button>
                <button
                  onClick={() => del(g.id, g.name)}
                  className="px-3 py-2 text-stone-500 hover:text-rose-400 transition-colors ml-auto"
                  title="Delete gallery"
                  data-testid={`gallery-delete-${g.id}`}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateGalleryModal
          onClose={() => setShowCreate(false)}
          onCreated={(g) => { setShowCreate(false); navigate(`/galleries/${g.id}`); }}
        />
      )}

      {renaming && (
        <RenameModal
          label="Rename Gallery"
          currentName={renaming.name}
          subtitle="Only changes the display name shown to admins and clients."
          testIdPrefix="gallery-rename"
          maxLen={120}
          onSave={async (newName) => {
            const r = await api.patch(`/galleries/${renaming.id}`, { name: newName });
            setGalleries((prev) => prev.map((x) => x.id === renaming.id ? { ...x, ...(r.data?.gallery || {}), name: newName } : x));
            toast.success("Gallery renamed");
          }}
          onClose={() => setRenaming(null)}
        />
      )}
    </div>
  );
}

function CreateGalleryModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [kind, setKind] = useState("client");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/galleries", { name: name.trim(), description: description.trim(), kind });
      toast.success("Gallery created");
      onCreated(r.data.gallery);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-4" onClick={onClose}>
      <div className="bg-stone-950 border border-stone-800 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="create-gallery-modal">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display uppercase text-2xl font-bold">New Gallery</h2>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-200"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <div className="label-tiny mb-1">Name</div>
            <AutocompleteInput
              value={name}
              onChange={setName}
              field="gallery_title"
              placeholder="e.g. Royal Crown Day 1 — Smith Family"
              className="field-input w-full"
              data-testid="new-gallery-name"
              autoFocus
            />
          </div>
          <div>
            <div className="label-tiny mb-1">Description (optional)</div>
            <AutocompleteInput
              as="textarea"
              field="description"
              value={description}
              onChange={setDescription}
              rows={3}
              className="field-input w-full resize-none"
              placeholder="A note your client will see at the top of their gallery."
              data-testid="new-gallery-description"
            />
          </div>
          <button type="submit" disabled={busy || !name.trim()} className="btn-primary w-full" data-testid="new-gallery-submit">
            {busy ? "Creating…" : "Create Gallery"}
          </button>
        </form>
      </div>
    </div>
  );
}
